// ────────────────────────────────────────────────────────────────
// Bandeau essai expiré — niveau 1 (ExecPro360 <-> abonné)
// Affiché uniquement si l'essai est terminé et qu'aucun abonnement
// payant n'est actif (server/utils/trialAccess.js). La consultation
// reste possible ; seules les écritures sont bloquées (402 côté API).
// ────────────────────────────────────────────────────────────────
import { useState } from "react";
import api from "../services/api";

// Déclenche le téléchargement d'un blob nommé — même pattern que
// FEC.jsx (seule route d'export déjà authentifiée du projet).
function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  Object.assign(document.createElement("a"), { href: url, download: filename }).click();
  URL.revokeObjectURL(url);
}

export default function TrialBanner({ user, company, onNavigate }) {
  const [exportingFec, setExportingFec] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [exportError, setExportError]   = useState("");

  if (!user?.trial?.blocked) return null;

  // Gel temporaire niveau 1 (EnovaStudio sans SIRET — voir
  // server/utils/trialAccess.js) : le paiement n'est pas encore ouvert.
  // On l'explique clairement plutôt que d'afficher un bouton qui échouerait.
  const billingFrozen = user?.trial?.billing_enabled === false;

  // Le choix du forfait (chantier "3 forfaits") se fait sur l'écran "Mon
  // abonnement" — POST /billing/checkout-session exige désormais un
  // plan_key, on ne peut plus lancer une session de paiement à l'aveugle
  // depuis ce bandeau.
  const handleUpgrade = () => onNavigate?.('monabonnement');

  // Portabilité RGPD / conservation comptable du prospect (10 ans, à sa
  // charge, pas à celle d'ExecPro360) avant l'archivage à J+21. Plage
  // couvrant toute la durée de vie du compte, pas une année civile.
  const handleExportFec = async () => {
    if (!company?.id) return;
    setExportError("");
    setExportingFec(true);
    try {
      const dateFrom = company.created_at ? company.created_at.slice(0, 10) : "2000-01-01";
      const dateTo   = new Date().toISOString().slice(0, 10);
      const { blob, filename } = await api.fec.exportRangeDownload(company.id, dateFrom, dateTo);
      triggerDownload(blob, filename);
    } catch (err) {
      setExportError(err.message || "Erreur lors de l'export FEC.");
    } finally {
      setExportingFec(false);
    }
  };

  const handleExportInvoicesCsv = async () => {
    if (!company?.id) return;
    setExportError("");
    setExportingCsv(true);
    try {
      const { blob, filename } = await api.invoices.exportCsvDownload(company.id);
      triggerDownload(blob, filename);
    } catch (err) {
      setExportError(err.message || "Erreur lors de l'export des factures.");
    } finally {
      setExportingCsv(false);
    }
  };

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 99998,
      background: "#b45309", color: "#fff", textAlign: "center",
      padding: "8px 16px", fontSize: 13, fontWeight: 700,
      display: "flex", alignItems: "center", justifyContent: "center", gap: 12,
      flexWrap: "wrap", boxShadow: "0 2px 8px rgba(180,83,9,0.5)",
    }}>
      <span>🟠 {user.trial.reason === "pilot_ended"
        ? `Votre période de client pilote s'est terminée le ${String(user.trial.pilot.until).split("-").reverse().join("/")} — consultation et exports disponibles, modification bloquée (support@execpro360.com)`
        : "Votre essai gratuit est terminé — consultation disponible, création/modification bloquée"}{exportError ? ` · ${exportError}` : ""}</span>
      <button
        onClick={handleExportFec}
        disabled={exportingFec || !company?.id}
        title="Télécharge toutes vos écritures comptables (format FEC, norme DGFiP) depuis la création de votre compte"
        style={{
          background: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,0.6)",
          borderRadius: 6, padding: "4px 12px", fontSize: 12, fontWeight: 700,
          cursor: exportingFec ? "not-allowed" : "pointer",
        }}
      >
        {exportingFec ? "Génération…" : "⬇ Export comptable (FEC)"}
      </button>
      <button
        onClick={handleExportInvoicesCsv}
        disabled={exportingCsv || !company?.id}
        title="Télécharge la liste de vos factures (numéro, date, client, montants, statut) au format CSV"
        style={{
          background: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,0.6)",
          borderRadius: 6, padding: "4px 12px", fontSize: 12, fontWeight: 700,
          cursor: exportingCsv ? "not-allowed" : "pointer",
        }}
      >
        {exportingCsv ? "Génération…" : "⬇ Liste de mes factures (CSV)"}
      </button>
      {billingFrozen ? (
        <span style={{
          background: "rgba(255,255,255,0.15)", borderRadius: 6,
          padding: "4px 12px", fontSize: 12, fontWeight: 800,
        }}>
          Les abonnements payants ouvrent prochainement
        </span>
      ) : (
        <button
          onClick={handleUpgrade}
          style={{
            background: "#fff", color: "#b45309", border: "none", borderRadius: 6,
            padding: "4px 12px", fontSize: 12, fontWeight: 800,
            cursor: "pointer",
          }}
        >
          Choisir mon forfait
        </button>
      )}
    </div>
  );
}
